import React from "react";
import { Spinner } from "@chakra-ui/react";
import { Button } from "newui/components/Button";
import { useIssueVerificationCodeMutation } from "./hooks/useIssueVerificationCodeMutation";

interface EmailConfirmationStepProps {
  /**
   * Inviter's email (from preflight) — when present, the "if this isn't
   * you" copy renders as a mailto link so the recipient can immediately
   * challenge the invitation. Falls back to plain copy when absent.
   */
  invitedByEmail?: string | null;
  invitedByName?: string | null;
  inviteToken: string;
  maskedEmail: string;
  /**
   * Called once the verification code email has been dispatched. The
   * code's server-side expiry timestamp (ISO-8601) is forwarded so the
   * verification step can show the exact "expires at HH:mm" datetime
   * instead of vague "1 hour" copy.
   */
  onCodeSent: (verificationExpiresAt: string | null) => void;
  /** Called when the issue-code call fails with a terminal error code. */
  onTerminalError: (code: string) => void;
}

/**
 * PRD step 3 — user confirms the destination email before any verification
 * code is sent. Splitting this from the silent on-mount preflight ensures:
 *   - No email is sent until the user takes explicit action.
 *   - StrictMode double-mount cannot dispatch duplicate emails.
 *   - The user sees the masked email and can abort if it isn't theirs.
 */
export const EmailConfirmationStep: React.FC<EmailConfirmationStepProps> = ({
  inviteToken,
  maskedEmail,
  invitedByEmail,
  invitedByName,
  onCodeSent,
  onTerminalError,
}) => {
  const issueCode = useIssueVerificationCodeMutation();

  const handleSendCode = () => {
    issueCode.mutate(
      { token: inviteToken },
      {
        onSuccess: (data) => {
          onCodeSent(data.verification_expires_at ?? null);
        },
        onError: (err) => {

          const errCode = err?.code ?? (err?.status === 429 ? "RATE_LIMIT_EXCEEDED" : "UNKNOWN_ERROR");

          onTerminalError(errCode);
        },
      }
    );
  };

  const inlineError =
    issueCode.isError && !issueCode.error?.code
      ? "Could not send the code. Please try again in a moment."
      : null;

  return (
    <div className="space-y-6" data-testid="email-confirmation-step">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Confirm your email</h2>
        <p className="mt-1 text-sm text-gray-600">
          We&apos;ll send a verification code to <strong>{maskedEmail}</strong>. If this
          isn&apos;t you, or you do not receive a verification code, please check your spam folder before{" "}
          {invitedByEmail ? (
            <>
              contacting{" "}
              <a
                href={`mailto:${invitedByEmail}?subject=${encodeURIComponent(
                  "Procuracon invitation"
                )}`}
                className="text-blue-600 underline hover:text-blue-700"
                data-testid="email-confirmation-inviter-mailto"
              >
                {invitedByName || invitedByEmail}
              </a>
              .
            </>
          ) : (
            <>contact your administrator.</>
          )}
        </p>
      </div>

      <Button
        type="button"
        onClick={handleSendCode}
        disabled={issueCode.isPending}
        className="w-full"
        data-testid="email-confirmation-send-code"
      >
        {issueCode.isPending ? <Spinner size="sm" /> : "Send code"}
      </Button>

      {inlineError && (
        <p className="text-sm text-red-600" role="alert" data-testid="email-confirmation-error">
          {inlineError}
        </p>
      )}
    </div>
  );
};
